import React from 'react';
import { BarChart2, Calendar, Download, Share2, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Card } from '../components/ui/Card';
import { DataTable } from '../components/ui/DataTable';
import { Input } from '../components/ui/Input';
import { useProperties } from '../hooks/useProperties';

// Analytics Components
import MetricsOverview from '../components/analytics/MetricsOverview';
import MarketTrends from '../components/analytics/MarketTrends';
import RevenueBreakdown from '../components/analytics/RevenueBreakdown';

export default function Analytics() {
  const {
    properties,
    filteredProperties,
    referenceProperty,
    setReferenceProperty,
    filters,
    setFilters,
    loading,
    error
  } = useProperties();

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-800 font-semibold">Error Loading Data</h2>
          <p className="text-red-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">Gas Station Analytics</h1>
        <div className="flex flex-wrap gap-2 md:gap-4">
          <Button variant="outline" className="text-sm md:text-base">
            <Calendar className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Last 30 Days</span>
            <span className="sm:hidden">30d</span>
          </Button>
          <Button variant="outline" className="text-sm md:text-base">
            <Download className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Export</span>
            <span className="sm:hidden">Exp</span>
          </Button>
          <Button variant="outline" className="text-sm md:text-base">
            <Share2 className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Share</span>
            <span className="sm:hidden">Shr</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <Card className="lg:col-span-1 p-4">
              <h2 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Reference Property</h2>
              <Select
                label="Select Property"
                value={referenceProperty?.id.toString() || ''}
                onChange={(value) => {
                  const selected = value ? properties.find(p => p.id.toString() === value) : null;
                  setReferenceProperty(selected || null);
                }}
                className="w-full"
              >
                <option value="">Select a property</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id.toString()}>
                    {property.location}
                  </option>
                ))}
              </Select>

              {referenceProperty && (
                <div className="mt-4 space-y-2">
                  <p><strong>Location:</strong> {referenceProperty.location}</p>
                  <p><strong>City:</strong> {referenceProperty.city}</p>
                  <p><strong>Size:</strong> {referenceProperty.size.toLocaleString()} sq ft</p>
                  <p><strong>Fuel Volume:</strong> {referenceProperty.fuelVolume.toLocaleString()} gallons</p>
                  <p><strong>C-Store Sales:</strong> ${referenceProperty.cstoreSales.toLocaleString()}</p>
                </div>
              )}
            </Card>

            <div className="lg:col-span-2">
              <MetricsOverview property={referenceProperty} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <MarketTrends data={filteredProperties} />
            <div className="lg:col-span-1">
              <Card className="p-4">
                <h3 className="text-lg font-semibold mb-4">Competitor Overview</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Interactive competitor mapping available in the dedicated{' '}
                  <a href="/dashboard/competitor-mapping" className="text-blue-600 hover:underline">
                    Competitor Mapping Dashboard
                  </a>
                </p>
                <div className="space-y-3">
                  {filteredProperties.slice(0, 5).map((property) => (
                    <div key={property.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-sm">{property.brand}</p>
                        <p className="text-xs text-muted-foreground">{property.address}, {property.city}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">{property.price}</p>
                        <p className="text-xs text-muted-foreground">{property.fuelVolume.toLocaleString()} gal</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-4"
                  onClick={() => window.location.href = '/dashboard/competitor-mapping'}
                >
                  Open Interactive Map
                </Button>
              </Card>
            </div>
          </div>

          <Card className="p-4">
            <RevenueBreakdown properties={filteredProperties} />
          </Card>
        </>
      )}
    </div>
  );
}